import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { format } from "date-fns";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const Monitor = () => {
  const [rawQuotations, setRawQuotations] = useState(null); // Changed from quotations
  const [processedQuotations, setProcessedQuotations] = useState(null); // New state for processed data
  const [doneQuotation, setDoneQuotation] = useState(false);
  const [allQuotationItemLists, setAllQuotationItemLists] = useState(null);
  const [allSalesOrders, setAllSalesOrders] = useState(null);
  const [allInvoiceRequests, setAllInvoiceRequests] = useState(null);
  const [doneFetchingDetails, setDoneFetchingDetails] = useState(false);
  const [tableSortBy, setTableSortBy] = useState('quotation_code');
  const [tableSortType, setTableSortType] = useState('ascending');

  useEffect(() => {
    getQuotations();
  }, []);

  useEffect(() => {
    if (doneQuotation) {
      fetchAllDetails();
    }
  }, [doneQuotation]);

  // useEffect to process quotations when all data is ready
  useEffect(() => {
    if (doneFetchingDetails && rawQuotations && allQuotationItemLists && allSalesOrders && allInvoiceRequests) {
      const newProcessedData = rawQuotations.map((quotation) => {
        const itemList = allQuotationItemLists.filter(item => item.quotation_id === quotation.id_quotation) || [];
        const salesOrders = allSalesOrders.filter(so => so.quotation_id === quotation.id_quotation) || [];
        const invoiceRequests = allInvoiceRequests.filter(ir => ir.quotation_id === quotation.id_quotation) || [];
        return {
          ...quotation,
          item_data: itemList,
          item_contract: itemList[0]?.contract_long_period || null,
          item_payment: itemList[0]?.billing_payment_period || null,
          item_period: (() => {
              const monthsPerTerm = {
                  'Monthly': 1,
                  'Bimonthly': 2,
                  'Quarterly': 3,
                  'Semesterly': 6,
                  'Annual': 12
              }[itemList[0]?.billing_payment_period] || itemList[0]?.contract_long_period;            
              return Math.ceil(itemList[0]?.contract_long_period / monthsPerTerm);
          })(),
          quotation_type: itemList[0]?.service_type || null,
          so_data: salesOrders,
          so_count: salesOrders.length,
          so_id: salesOrders.map(so => so.id_so).join('\n') || null,
          so_code: salesOrders.map(so => so.so_code).join('\n') || null,
          so_status: salesOrders.map(so => so.so_status).join('\n') || null,
          iou_data: invoiceRequests,
          iou_count: invoiceRequests.length,
          iou_id: invoiceRequests.map((ir, index) => `<span class="d-inline-block w-15 text-end">${index + 1}.</span> ${ir.id_iou ?? ""}`).join('\n') || null,
          iou_period: invoiceRequests.map((ir, index) => `<span class="d-inline-block w-15 text-end">${index + 1}.</span> ${ir.id_iou ? (`<span class="d-inline-block text-end w-80 me-1">${format(ir.start_term_period, 'd MMM yyyy')}</span>-<span class="d-inline-block w-80 ms-1">${format(ir.end_term_period, 'd MMM yyyy')}</span>`) : ""}`).join('\n') || null,
          iou_months: invoiceRequests.map((ir, index) => `${ir.id_iou ? (`<span class="d-inline-block text-end w-15">${ir.number_of_months}</span> Month(s)`) : ""}`).join('\n') || null,
          iou_sum: invoiceRequests.reduce((sum, ir) => sum + (parseFloat(ir.iou_subtotal) || 0), 0), // Calculate sum by parsing string values to float
          iou_code: invoiceRequests.map((ir, index) => `${ir.iou_code ?? ""}`).join('\n') || null,
          iou_status: invoiceRequests.map((ir, index) => `${ir.iou_status ? ir.iou_status.replace('In Progress', 'New') : ""}`).join('\n') || null,
          iou_invoice: invoiceRequests.map((ir, index) => `${ir.invoice_number ?? ""}`).join('\n') || null,
          iou_subtotal: invoiceRequests.map((ir, index) => `${ir.iou_subtotal ? priceFormat(ir.iou_subtotal, ir.iou_currency) : ""}`).join('\n') || null,
          iou_paid: invoiceRequests.map((ir, index) => `${ir.payment_input_date ? format(ir.payment_input_date, 'd MMM yyyy') : ""}`).join('\n') || null,
        };
      });
      setProcessedQuotations(newProcessedData);
    }
  }, [doneFetchingDetails, rawQuotations, allQuotationItemLists, allSalesOrders, allInvoiceRequests]);

  const getQuotations = async () => {
    try {
      const requestQuotations = {};
      const fetchQuotations = await jwtFetch(config.ENDPOINT + 'get/quotations', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestQuotations)
      });
      const responseGetQuotations = await fetchQuotations.json();
      if (responseGetQuotations.status === 200) {
        setRawQuotations(responseGetQuotations.data); // Changed to setRawQuotations
        setDoneQuotation(true);
      } else {
        alert(responseGetQuotations.message);
      }
    } catch (error) {
      console.error('Error in getQuotations:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const fetchAllDetails = async () => {
    const [itemLists, salesOrdersData, invoiceRequestsData] = await Promise.all([
      getAllQuotationItemLists(),
      getAllSalesOrders(),
      getAllInvoiceRequests(),
    ]);
    setAllQuotationItemLists(itemLists || []);
    setAllSalesOrders(salesOrdersData || []);
    setAllInvoiceRequests(invoiceRequestsData || []);
    setDoneFetchingDetails(true);
  };
  
  const getAllQuotationItemLists = async () => {
    try {
      // Assuming API returns all items if quotation_id is not provided or is null
      const requestQuotationItemList = {}; // Empty or adapt if API needs specific flag
      const fetchQuotationItemList = await jwtFetch(config.ENDPOINT + 'get/quotation_item_list', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestQuotationItemList)
      });
      const responseGetQuotationItemList = await fetchQuotationItemList.json();
      if (responseGetQuotationItemList.status === 200) {
        return responseGetQuotationItemList.data;
      } else {
        console.error("Error fetching all quotation item lists:", responseGetQuotationItemList.message);
        return null;
      }
    } catch (error) {
      console.error('Error in getAllQuotationItemLists:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getAllSalesOrders = async () => {
    try {
      // Assuming API returns all items if quotation_id is not provided or is null
      const requestSalesOrders = {}; // Empty or adapt if API needs specific flag
      const fetchSalesOrders = await jwtFetch(config.ENDPOINT + 'get/so', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestSalesOrders)
      });
      const responseGetSalesOrders = await fetchSalesOrders.json();
      if (responseGetSalesOrders.status === 200) {
        return responseGetSalesOrders.data;
      } else {
        console.error("Error fetching all sales orders:", responseGetSalesOrders.message);
        return null;
      }
    } catch (error) {
      console.error('Error in getAllSalesOrders:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getAllInvoiceRequests = async () => {
    try {
      // Assuming API returns all items if quotation_id is not provided or is null
      const requestInvoiceRequests = {}; // Empty or adapt if API needs specific flag
      const fetchInvoiceRequests = await jwtFetch(config.ENDPOINT + 'get/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestInvoiceRequests)
      });
      const responseGetInvoiceRequests = await fetchInvoiceRequests.json();
      if (responseGetInvoiceRequests.status === 200) {
        return responseGetInvoiceRequests.data;
      } else {
        console.error("Error fetching all invoice requests:", responseGetInvoiceRequests.message);
        return null;
      }
    } catch (error) {
      console.error('Error in getAllInvoiceRequests:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  // Remove old getQuotationItemList, getSalesOrders, getInvoiceRequests as they are replaced by getAll* versions
  // and data is filtered from the all* states.
  // If they are used elsewhere, they should be kept or refactored.
  // For this specific optimization, they are no longer called within updateQuotations.

  const sortTable = (sortBy) => {
    if (tableSortBy === sortBy) {
      setTableSortType(tableSortType === 'ascending' ? 'descending' : 'ascending');
    } else {
      setTableSortBy(sortBy);
      setTableSortType('ascending');
    }
  }

  return (
    <div id="monitor">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
            </div>
            <hr className="m-0" />
            <Menu active="dashboard" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">

                <section>
                  <div className="table-responsive mb-2" style={{ maxHeight: '78vh', overflow: 'auto' }}>
                    <table className="table table-hover mb-0" style={{ tableLayout: 'auto', width: '100%' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                        <tr>
                          <th className="bg-gold w-40" style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 4, backgroundColor: 'var(--bs-gold)' }}>#</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '150px', position: 'sticky', left: '40px', zIndex: 4, backgroundColor: 'var(--bs-gold)' }} onClick={() => sortTable('quotation_code')}>QT Number{tableSortBy === 'quotation_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '80px' }} onClick={() => sortTable('quotation_status')}>QT Status {tableSortBy === 'quotation_status' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '80px' }} onClick={() => sortTable('quotation_approval')}>QT Approval {tableSortBy === 'quotation_approval' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '80px' }} onClick={() => sortTable('quotation_type')}>QT Type {tableSortBy === 'quotation_type' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '80px' }} onClick={() => sortTable('item_contract')}>QT Contract {tableSortBy === 'item_contract' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '80px' }} onClick={() => sortTable('item_payment')}>QT Payment {tableSortBy === 'item_payment' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '80px' }} onClick={() => sortTable('item_period')}>QT Period {tableSortBy === 'item_period' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '150px' }} onClick={() => sortTable('quotation_total')}>QT Total {tableSortBy === 'quotation_total' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '150px' }} onClick={() => sortTable('so_code')}>SO Number {tableSortBy === 'so_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '100px' }} onClick={() => sortTable('so_status')}>SO Status {tableSortBy === 'so_status' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '220px' }} onClick={() => sortTable('iou_period')}>IR Period {tableSortBy === 'iou_period' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '100px' }} onClick={() => sortTable('iou_months')}>IR Months {tableSortBy === 'iou_months' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '200px' }} onClick={() => sortTable('iou_code')}>IR Number {tableSortBy === 'iou_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '120px' }} onClick={() => sortTable('iou_status')}>IR Status {tableSortBy === 'iou_status' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '120px' }} onClick={() => sortTable('iou_invoice')}>IR Invoice {tableSortBy === 'iou_invoice' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '150px' }} onClick={() => sortTable('iou_subtotal')}>IR Total {tableSortBy === 'iou_subtotal' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '150px' }} onClick={() => sortTable('iou_sum')}>IR Sum {tableSortBy === 'iou_sum' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                          <th className="bg-gold pointer" style={{ whiteSpace: 'nowrap', minWidth: '100px' }} onClick={() => sortTable('iou_paid')}>IR Paid {tableSortBy === 'iou_paid' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {processedQuotations && processedQuotations // Changed to processedQuotations
                          .sort((a, b) => {
                            const valA = a[tableSortBy] !== null && a[tableSortBy] !== undefined ? a[tableSortBy].toString() : '';
                            const valB = b[tableSortBy] !== null && b[tableSortBy] !== undefined ? b[tableSortBy].toString() : '';
                            return tableSortType === 'ascending' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                          })
                          .map((quotation, q) => (
                            <tr key={"quotation-" + q}>
                              <td valign="top" style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, backgroundColor: 'var(--bs-table-bg)', zIndex: 1 }}>{q + 1}</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap', position: 'sticky', left: '40px', backgroundColor: 'var(--bs-table-bg)', zIndex: 1 }}><a href={"/quotation/detail/" + quotation.id_quotation} target="_blank" rel="noreferrer">{quotation.quotation_code}</a></td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }}>{quotation.quotation_status}</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }}>{(quotation.quotation_approval !== 'Draft' && quotation.quotation_status === 'Open') && quotation.quotation_approval}</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }}>{quotation.quotation_type}</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }}><span className="d-inline-block w-15 text-end">{quotation.item_contract}</span> Month(s)</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }}>{quotation.item_payment}</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }}><span className="d-inline-block w-15 text-end">{quotation.item_period}</span> Schedule(s)</td>
                              <td valign="top" style={{ whiteSpace: 'nowrap' }} align="right">{priceFormat(quotation.quotation_total, quotation.quotation_currency)}</td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }}>{quotation.so_code}</td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }}>{quotation.so_status}</td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_period }}></td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_months }}></td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_code }}></td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_status }}></td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_invoice }}></td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_subtotal }} align="right"></td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} align="right" className={quotation.iou_sum && quotation.quotation_total && quotation.iou_sum !== parseFloat(quotation.quotation_total) ? 'text-danger' : ''}>{quotation.iou_sum ? priceFormat(quotation.iou_sum, quotation.quotation_currency) : ""}</td>
                              <td valign="top" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: quotation.iou_paid }}></td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Monitor;


